import React, { useMemo } from 'react';

import { Form } from 'antd-v5';
import { keyBy } from 'lodash';
import { DateTime } from 'luxon';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import type { PostUnapproveAdvancementRequest } from '@appTypes/esb';
import { routeParamsSel } from '@modules/location/duck/selectorsTyped';
import { goToUnitRosterYouthRank } from '@modules/roster-lite/duck/actions';
import type { RouteUnitRosterYouthRank } from '@modules/roster-lite/routes.types';
import {
  useGetRanksQuery,
  useUnapproveYouthAwardMutation,
  useUpdateYouthAdvancementMutation,
} from '@modules/rtk/esb-api';
import { toastService } from '@modules/toasts';
import {
  useGetYouthAdventuresQuery,
  useGetYouthAwardsQuery,
  useGetYouthMeritBadgesQuery,
  useGetYouthRanksQuery,
} from '@modules/youthProfile/api';
import { unapproveAdvancementTypes } from '@shared/constants';
import type { AdvancementTypesType } from '@shared/constants';
import {
  organizationGuidSel,
  userIdSel,
} from '@shared/duck/selectors/general.selectors';
import { intl } from '@shared/localization';
import { RankModel } from '@shared/utils/ranksModel';

import AdvancementDetails from './AdvancementDetails';
import { buildAdvancementSave } from './buildAdvancementSave';
import { AdvancementAction, FormField } from './types';
import type {
  AdvSummaryFormData,
  AdvancementDetailsContainerProps,
  AdvancementDetailsData,
  AdvancementDetailsNavigation,
} from './types';

/**
 * Union of fields the four per-youth list items carry; each type names its id and
 * completion date differently, so selection goes through the resolvers below
 */
interface RawAdvancement extends Partial<AdvancementDetailsData> {
  awardId?: number;
  adventureId?: number;
  adventureName?: string;
  dateEarned?: string | null;
  dateCompleted?: string | null;
}

const resolveId = (item?: RawAdvancement) =>
  item?.id ?? item?.awardId ?? item?.adventureId;

const resolveName = (item?: RawAdvancement) =>
  item?.name ?? item?.adventureName ?? '';

/** Fetches the advancement, wires rank nav, and saves through RTK; owns the `<Form>` */
export default function AdvancementDetailsContainer(
  props: AdvancementDetailsContainerProps,
) {
  const {
    type,
    advancementId,
    userId,
    visibleActions,
    disabledActions,
    extraContent,
    onSave,
  } = props;

  const dispatch = useDispatch();
  const { orgGuid } = useSelector(routeParamsSel<RouteUnitRosterYouthRank>);
  const organizationGuid = useSelector(organizationGuidSel);
  // The signed-in user recording the change
  const currentUserId = useSelector(userIdSel);

  // Only the query for this type runs; a supplied raw prop skips them all
  const rawFromProp =
    type === 'ranks'
      ? props.rank
      : type === 'meritBadges'
      ? props.meritBadge
      : type === 'awards'
      ? props.award
      : props.adventure;
  const skipAll = !userId || !!rawFromProp;

  const ranksQuery = useGetYouthRanksQuery(
    { userId, params: { status: 'all' } },
    { skip: skipAll || type !== 'ranks' },
  );
  const mbQuery = useGetYouthMeritBadgesQuery(
    { userId, params: { status: 'all' } },
    { skip: skipAll || type !== 'meritBadges' },
  );
  const awardsQuery = useGetYouthAwardsQuery(
    { userId, params: { status: 'all' } },
    { skip: skipAll || type !== 'awards' },
  );
  const adventuresQuery = useGetYouthAdventuresQuery(
    { userId, params: { status: 'all' } },
    { skip: skipAll || type !== 'adventures' },
  );

  const isSameId = (id?: number | string) =>
    id != null && String(id) === String(advancementId);

  const asRaw = (items?: readonly unknown[]) =>
    (items ?? []) as readonly RawAdvancement[];

  // Ranks come nested under `program[]`; the others are flat lists
  const rankProgram = ranksQuery.data?.program?.find(program =>
    asRaw(program.ranks).some(rank => isSameId(resolveId(rank))),
  );

  const raw = useMemo<RawAdvancement | undefined>(() => {
    if (rawFromProp) return rawFromProp as RawAdvancement;
    const find = (items?: readonly unknown[]) =>
      asRaw(items).find(item => isSameId(resolveId(item)));
    switch (type) {
      case 'ranks':
        return find(rankProgram?.ranks);
      case 'meritBadges':
        return find(mbQuery.data);
      case 'awards':
        return find(awardsQuery.data);
      case 'adventures':
        return find(adventuresQuery.data);
      default:
        return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    rawFromProp,
    type,
    rankProgram,
    mbQuery.data,
    awardsQuery.data,
    adventuresQuery.data,
    advancementId,
  ]);

  const usesDateEarned = type === 'ranks' || type === 'awards';
  const completionDate = usesDateEarned ? raw?.dateEarned : raw?.dateCompleted;

  // Source the rank image from the lookup: the youth's profile may omit the rank,
  // which would leave the image blank
  const rankDictQuery = useGetRanksQuery(undefined, {
    skip: type !== 'ranks',
  });
  const rankDict = rankDictQuery.data;
  const ranksById = useMemo(() => keyBy(rankDict ?? [], 'id'), [rankDict]);
  const rankImageUrl = useMemo(() => {
    if (type !== 'ranks') return raw?.imageUrl100;
    const rank = ranksById[String(advancementId)];
    const version = rank?.versions?.find(v => v.active) ?? rank?.versions?.[0];
    return version?.imageUrl100 ?? raw?.imageUrl100;
  }, [type, ranksById, advancementId, raw]);

  /**
   * The skeleton is a first load only, where there is nothing to show yet. A later
   * refetch - the one a save sets off, say - keeps the rendered card in place and
   * dims it instead, so the column never reflows
   */
  const isLoading =
    ranksQuery.isLoading ||
    mbQuery.isLoading ||
    awardsQuery.isLoading ||
    adventuresQuery.isLoading ||
    rankDictQuery.isLoading;

  const isFetching =
    !isLoading &&
    (ranksQuery.isFetching ||
      mbQuery.isFetching ||
      awardsQuery.isFetching ||
      adventuresQuery.isFetching ||
      rankDictQuery.isFetching);

  const initialValues = useMemo<AdvSummaryFormData>(() => {
    const base: AdvSummaryFormData = {
      [FormField.CompletionDate]: completionDate
        ? DateTime.fromISO(completionDate)
        : null,
      [FormField.Approved]: !!raw?.leaderApprovedDate,
      [FormField.CounselorApproved]: !!raw?.counselorApprovedDate,
      [FormField.Awarded]: !!raw?.awardedDate,
    };
    // The raw record rides along under its type key so the submit handler keeps the metadata
    if (type === 'ranks') base.rank = raw as AdvSummaryFormData['rank'];
    else if (type === 'meritBadges')
      base.meritBadge = raw as AdvSummaryFormData['meritBadge'];
    else if (type === 'awards') base.award = raw as AdvSummaryFormData['award'];
    else base.adventure = raw as AdvSummaryFormData['adventure'];
    return base;
  }, [completionDate, raw, type]);

  // The container only adds the data-driven lock: the date is fixed once approved or awarded
  const resolvedDisabled = useMemo<AdvancementAction[]>(() => {
    const set = new Set<AdvancementAction>(disabledActions ?? []);
    if (raw?.leaderApprovedDate || raw?.awardedDate)
      set.add(AdvancementAction.CompletionDate);
    return [...set];
  }, [disabledActions, raw]);

  const navigation = useMemo<AdvancementDetailsNavigation | undefined>(() => {
    if (type !== 'ranks') return undefined;
    // Prev/next follow the canonical level-ordered ladder via RankModel, not the fetched order
    const rankId = Number(advancementId);
    const programId = RankModel.getProgramIdForRank(rankId);
    if (!programId) return undefined;
    let model: RankModel;
    try {
      model = new RankModel(programId, rankId);
    } catch {
      return undefined;
    }
    const prev = model.isFirstOfProgram() ? undefined : model.getPreviousRank();
    const next = model.isLastOfProgram() ? undefined : model.getNextRank();
    const goTo = (id: number) =>
      dispatch(goToUnitRosterYouthRank({ orgGuid, userId, rankId: id }));
    // The whole ladder, level-ordered, rendered as jump-to dots
    const steps = model.programRanks.map(rank => ({
      id: rank.id,
      name: rank.name,
      active: rank.id === rankId,
    }));
    return {
      hasPrev: !!prev,
      hasNext: !!next,
      prevLabel: prev?.name,
      nextLabel: next?.name,
      onPrev: prev ? () => goTo(prev.id) : undefined,
      onNext: next ? () => goTo(next.id) : undefined,
      steps,
      onSelectStep: goTo,
    };
  }, [type, advancementId, orgGuid, userId, dispatch]);

  const [updateAdvancement, { isLoading: updating }] =
    useUpdateYouthAdvancementMutation();
  const [unapprove, { isLoading: unapproving }] =
    useUnapproveYouthAwardMutation();

  const handleFinish = async (values: AdvSummaryFormData) => {
    if (onSave) return onSave(values);
    if (!raw) return;

    // Hidden controls (e.g. a page showing only the completion date) don't submit
    // values; buildAdvancementSave treats those absent fields as unchanged
    const result = buildAdvancementSave({
      type,
      leaderUserId: Number(currentUserId),
      organizationGuid,
      values,
      initial: initialValues,
    });

    try {
      if (result.kind === 'unapprove') {
        await unapprove({
          organizationGuid,
          advancementType: type,
          body: [
            {
              userId: Number(userId),
              organizationGuid,
              advancementId: Number(advancementId),
              advancementType: unapproveAdvancementTypes[type],
              newStatus: result.newStatus,
            },
          ] as PostUnapproveAdvancementRequest,
        }).unwrap();
      } else {
        await updateAdvancement({
          userId,
          advancementType: type as AdvancementTypesType,
          advancementId,
          organizationGuid,
          body: result.body,
        }).unwrap();
      }
      toastService.success(
        <FormattedMessage id="youthProfile.ActionBar.advancementUpdate" />,
      );
    } catch {
      toastService.error(intl.formatMessage({ id: 'shared.errors.generic' }));
    }
  };

  const advancement: AdvancementDetailsData = {
    id: resolveId(raw) ?? advancementId,
    name: resolveName(raw),
    imageUrl100: rankImageUrl,
    status: raw?.status,
    percentCompleted: raw?.percentCompleted,
    markedCompletedDate: raw?.markedCompletedDate,
    markedCompletedFirstName: raw?.markedCompletedFirstName,
    markedCompletedLastName: raw?.markedCompletedLastName,
    counselorApprovedDate: raw?.counselorApprovedDate,
    counselorApprovedFirstName: raw?.counselorApprovedFirstName,
    counselorApprovedLastName: raw?.counselorApprovedLastName,
    leaderApprovedDate: raw?.leaderApprovedDate,
    leaderApprovedFirstName: raw?.leaderApprovedFirstName,
    leaderApprovedLastName: raw?.leaderApprovedLastName,
    awardedDate: raw?.awardedDate,
    awardedFirstName: raw?.awardedFirstName,
    awardedLastName: raw?.awardedLastName,
  };

  return (
    // Keyed so the Form remounts both on prev/next AND once the record loads: antd
    // applies initialValues only on mount, so the key must change when the rank changes
    // and when its data first arrives. Without the ready/loading part the Form mounts
    // during the loading render (no dates yet) and the completion date stays empty after load
    // DO NOT REMOVE the key prop
    <Form
      key={`${type}-${advancementId}-${raw ? 'ready' : 'loading'}`}
      layout="vertical"
      initialValues={initialValues}
      onFinish={handleFinish}
    >
      <AdvancementDetails
        type={type}
        advancement={advancement}
        loading={isLoading}
        busy={isFetching}
        saving={updating || unapproving}
        visibleActions={visibleActions}
        disabledActions={resolvedDisabled}
        navigation={navigation}
        extraContent={extraContent}
      />
    </Form>
  );
}
